import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const DOC_TYPES = [
  ['liability_release', 'Liability release / waiver'],
  ['boarding_agreement', 'Boarding agreement'],
  ['training_agreement', 'Lesson / training agreement'],
  ['coggins_notice', 'Coggins requirement notice'],
  ['emergency_auth', 'Emergency contact & vet authorization'],
  ['barn_rules', 'Barn rules / handbook'],
  ['facility_rules', 'Facility / arena rules'],
  ['insurance_cert', 'Insurance certificate'],
  ['media_release', 'Media release'],
  ['other', 'Other'],
];

// Member completes a sign OR acknowledge document — both capture full audit trail
const CompleteModal = ({ doc, onClose, onDone }) => {
  const isAck = doc.action_type === 'acknowledge';
  const [legalName, setLegalName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const start = (e) => { drawing.current = true; draw(e); };
  const end = () => { drawing.current = false; if (canvasRef.current) canvasRef.current.getContext('2d').beginPath(); };
  const draw = (e) => {
    if (!drawing.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#340925';
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); };

  const handleComplete = async () => {
    if (!legalName.trim()) return toast.error('Type your legal name');
    if (!agreed) return toast.error(isAck ? 'You must acknowledge' : 'You must agree to sign');
    setSaving(true);
    try {
      const signature_image = isAck ? null : canvasRef.current.toDataURL();
      await api.post(`/org-documents/${doc.id}/sign`, { legal_name: legalName, signature_image, agreed: true });
      toast.success(isAck ? 'Acknowledged' : 'Signed');
      onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2>{isAck ? 'Acknowledge' : 'Sign'}: {doc.title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-full" style={{ marginBottom: 14 }}>📄 Read the document</a>
        <div className="form-group">
          <label className="form-label">Type your legal name *</label>
          <input className="form-input" value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Full legal name" autoComplete="off" />
        </div>
        {!isAck && (
          <div className="form-group">
            <label className="form-label">Draw your signature</label>
            <canvas ref={canvasRef} width={420} height={120}
              style={{ width: '100%', height: 120, border: '1px solid var(--border-color)', borderRadius: 8, touchAction: 'none', background: '#fafafa' }}
              onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={draw} onTouchEnd={end} />
            <button className="btn btn-secondary btn-sm" onClick={clear} style={{ marginTop: 6 }}>Clear</button>
          </div>
        )}
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13, marginBottom: 14 }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ accentColor: 'var(--cr-primary)', marginTop: 2 }} />
          {isAck ? doc.acknowledge_text : 'I have read and agree to this document. I understand this is a legally binding electronic signature.'}
        </label>
        <button className="btn btn-primary btn-full" onClick={handleComplete} disabled={saving}>
          {saving ? 'Saving...' : (isAck ? 'Acknowledge' : 'Sign document')}
        </button>
      </div>
    </div>
  );
};

const UploadModal = ({ orgId, members, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: '', doc_type: '', action_type: 'read',
    acknowledge_text: 'By acknowledging this document, I have read, understand, and agree to follow this document.',
    assign_mode: 'all', notify_members: true,
    expiration_type: 'perpetual', expiration_interval_days: 365,
  });
  const [assignees, setAssignees] = useState([]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const toggleAssignee = (id) => setAssignees(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleUpload = async () => {
    if (!form.title.trim()) return toast.error('Title required');
    if (!form.doc_type) return toast.error('Choose a document type');
    if (!file) return toast.error('Choose a file');
    if (form.assign_mode === 'selected' && assignees.length === 0) return toast.error('Select at least one member');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (form.assign_mode === 'selected') fd.append('assignee_ids', JSON.stringify(assignees));
      fd.append('file', file);
      await api.post(`/org-documents/org/${orgId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Upload document</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Liability Release 2026" autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Document type *</label>
          <select className="form-input" value={form.doc_type} onChange={e => setForm(p => ({ ...p, doc_type: e.target.value }))}>
            <option value="">Select a type</option>
            {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">File (PDF) *</label>
          <button className="btn btn-secondary btn-full" onClick={() => fileRef.current.click()}>{file ? file.name : 'Choose file'}</button>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
        </div>

        <div className="form-group">
          <label className="form-label">What do members need to do?</label>
          <div className="chip-group">
            {[['read', 'Read only'], ['acknowledge', 'Acknowledge'], ['sign', 'Sign']].map(([v, l]) => (
              <div key={v} className={`chip ${form.action_type === v ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, action_type: v }))}>{l}</div>
            ))}
          </div>
        </div>

        {form.action_type === 'acknowledge' && (
          <div className="form-group">
            <label className="form-label">Acknowledgment statement</label>
            <textarea className="form-input" rows={2} value={form.acknowledge_text} onChange={e => setForm(p => ({ ...p, acknowledge_text: e.target.value }))} />
          </div>
        )}

        {form.action_type !== 'read' && (
          <>
            <div className="form-group">
              <label className="form-label">Who needs to {form.action_type}?</label>
              <div className="chip-group">
                <div className={`chip ${form.assign_mode === 'all' ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, assign_mode: 'all' }))}>All active members</div>
                <div className={`chip ${form.assign_mode === 'selected' ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, assign_mode: 'selected' }))}>Select members</div>
              </div>
            </div>
            {form.assign_mode === 'selected' && (
              <div className="form-group" style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 8, padding: 8 }}>
                {members.filter(m => m.role !== 'manager').map(m => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={assignees.includes(m.account_id)} onChange={() => toggleAssignee(m.account_id)} style={{ accentColor: 'var(--cr-primary)' }} />
                    {m.account?.first_name} {m.account?.last_name?.[0]}.
                  </label>
                ))}
                {members.filter(m => m.role !== 'manager').length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No members yet</div>}
              </div>
            )}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, marginBottom: 14 }}>
              <input type="checkbox" checked={form.notify_members} onChange={e => setForm(p => ({ ...p, notify_members: e.target.checked }))} style={{ accentColor: 'var(--cr-primary)' }} />
              Notify members now
            </label>
            <div className="form-group">
              <label className="form-label">Expiration</label>
              <select className="form-input" value={form.expiration_type} onChange={e => setForm(p => ({ ...p, expiration_type: e.target.value }))}>
                <option value="perpetual">Never expires</option>
                <option value="interval">Expires after a period</option>
              </select>
            </div>
            {form.expiration_type === 'interval' && (
              <div className="form-group">
                <label className="form-label">Valid for (days)</label>
                <input className="form-input" type="number" value={form.expiration_interval_days} onChange={e => setForm(p => ({ ...p, expiration_interval_days: e.target.value }))} />
              </div>
            )}
          </>
        )}

        <button className="btn btn-primary btn-full" onClick={handleUpload} disabled={saving}>{saving ? 'Uploading...' : 'Upload'}</button>
      </div>
    </div>
  );
};

const OrgDocumentsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [isController, setIsController] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [docsRes, myOrgs] = await Promise.all([
        api.get(`/org-documents/org/${id}`),
        api.get('/organizations/mine'),
      ]);
      setDocs(docsRes.data);
      const mine = myOrgs.data.find(o => o.id === id);
      const controller = mine && mine.my_role === 'manager';
      setIsController(controller);
      if (controller) {
        try { const m = await api.get(`/organizations/${id}/members`); setMembers(m.data); } catch {}
      }
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const handleNotify = async (docId) => {
    try {
      const { data } = await api.post(`/org-documents/${docId}/notify`, {});
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const statusBadge = (d) => {
    if (d.action_type === 'read') return <span className="badge badge-gray">Read only</span>;
    if (d.my_status === 'done') return <span className="badge badge-green">✓ {d.action_type === 'acknowledge' ? 'Acknowledged' : 'Signed'}</span>;
    if (d.my_status === 'pending') return <span className="badge badge-red">{d.action_type === 'acknowledge' ? 'Needs acknowledgment' : 'Needs signature'}</span>;
    if (d.my_status === 'expired') return <span className="badge badge-amber">Expired — redo</span>;
    return null;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(`/organizations/${id}`)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>Documents</h1>
          {isController && (
            <button onClick={() => setUploading(true)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 13, borderRadius: 8, padding: '6px 14px', fontWeight: 500 }}>+ Upload</button>
          )}
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {loading ? (
          <div className="loading-screen"><div className="spinner" /></div>
        ) : docs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📄</div>
            <h3>No documents</h3>
            <p>{isController ? 'Upload liability releases, agreements, rules, and more.' : 'No documents have been shared yet.'}</p>
          </div>
        ) : (
          docs.map(doc => (
            <div key={doc.id} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>📄</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{doc.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {doc.action_type === 'read' ? 'Read only' : doc.action_type === 'acknowledge' ? 'Acknowledgment' : 'Signature'}
                    {doc.assign_mode === 'selected' ? ' · selected members' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {statusBadge(doc)}
                <div style={{ flex: 1 }} />
                {!isController && doc.action_type !== 'read' && doc.my_status !== 'done' && (
                  <button className="btn btn-primary btn-sm" onClick={() => setCompleting(doc)}>{doc.action_type === 'acknowledge' ? 'Acknowledge' : 'Sign now'}</button>
                )}
                {isController && doc.action_type !== 'read' && (
                  <>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleNotify(doc.id)}>Notify</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/organizations/${id}/documents/${doc.id}/compliance`)}>Who's done</button>
                  </>
                )}
                <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">View</a>
              </div>
            </div>
          ))
        )}
      </div>

      {completing && <CompleteModal doc={completing} onClose={() => setCompleting(null)} onDone={() => { setCompleting(null); load(); }} />}
      {uploading && <UploadModal orgId={id} members={members} onClose={() => setUploading(false)} onSaved={() => { setUploading(false); load(); }} />}
    </div>
  );
};

export default OrgDocumentsPage;
